import {
  payWithCoinbaseApp,
  Session,
  waitForSession,
  CoinbaseError,
} from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { useEffect, useState, useRef } from "react";
import Alert from "../components/Alert";
import Warning from "../components/Warning";
import { Clock, LockIcon, CircleCheck } from "lucide-react";
import PrimaryButton from "../components/PrimaryButton";
import { logger } from "../lib/logger";
import { minUSDAmountByPaymentMethod } from "../lib/minUSDAmountByPaymentMethod";
import { isOverpaymentAboveThreshold } from "../lib/overpayment";

type Props = {
  onBack: () => void;
  session: Session;
  accessToken: string;
  onSuccess: (session: Session) => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
  onOverpaymentRefund?: (session: Session) => void;
};

export default function PayWithCoinbaseApp({
  onBack,
  session,
  accessToken,
  onSuccess,
  onInsufficientPaymentAmount,
  onOverpaymentRefund,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  const [mfaCode, setMfaCode] = useState<string>("");

  const [step, setStep] = useState<
    "mfa_code" | "transferring" | "waiting_for_transfer" | "wait_for_session"
  >("mfa_code");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");
  const [waitingCopyStage, setWaitingCopyStage] = useState<
    "initial" | "initiated" | "confirming" | "delayed"
  >("initial");

  const didTriggerOverpaymentRef = useRef(false);

  const minAmountUSD = minUSDAmountByPaymentMethod.coinbase_app;
  const sessionAmountUSD = session.paymentAmountUSD
    ? Number(session.paymentAmountUSD)
    : 0;
  const isBelowMinAmount = sessionAmountUSD < minAmountUSD;

  const onTransfer = async () => {
    try {
      setStep("transferring");
      setErrorMessage("");

      await payWithCoinbaseApp(glideConfig, {
        sessionId: session.sessionId,
        accessToken,
        mfaCode,
      });

      setStep("waiting_for_transfer");
      setWaitingCopyStage("initial");

      await waitForSession(glideConfig, {
        sessionId: session.sessionId,
        onUpdate: (updatedSession) => {
          setPaymentStatus(updatedSession.paymentStatus);

          if (updatedSession.paymentStatus === "paid") {
            onSuccess(updatedSession);
          }
          if (
            depositConfig.mode === "pay" &&
            updatedSession.paymentStatus === "pending_refund" &&
            onInsufficientPaymentAmount
          ) {
            onInsufficientPaymentAmount(updatedSession);
            return;
          }

          // Detect overpayment - only in pay mode, only after funds arrive
          const isFunded =
            updatedSession.paymentStatus === "waiting_confirmations" ||
            updatedSession.paymentStatus === "paid";

          if (
            depositConfig.mode === "pay" &&
            isFunded &&
            onOverpaymentRefund &&
            updatedSession.paymentAmountUSD &&
            updatedSession.actualPaymentAmountUSD &&
            !didTriggerOverpaymentRef.current
          ) {
            const requiredAmount = Number(updatedSession.paymentAmountUSD);
            const paidAmount = Number(updatedSession.actualPaymentAmountUSD);

            if (isOverpaymentAboveThreshold(requiredAmount, paidAmount)) {
              didTriggerOverpaymentRef.current = true;
              onOverpaymentRefund(updatedSession);
            }
          }

          window.parent?.postMessage(
            { type: "sessionUpdate", session: updatedSession },
            "*",
          );
        },
      });
    } catch (error) {
      setStep("mfa_code");
      setMfaCode("");

      if (error instanceof CoinbaseError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("");
      }

      logger.error(`${error}`);
    }
  };

  useEffect(() => {
    if (step !== "waiting_for_transfer") return;

    const initiatedTimeout = setTimeout(
      () => setWaitingCopyStage("initiated"),
      40000,
    );
    const confirmingTimeout = setTimeout(
      () => setWaitingCopyStage("confirming"),
      70000,
    );
    const delayedTimeout = setTimeout(
      () => setWaitingCopyStage("delayed"),
      110000,
    );

    return () => {
      clearTimeout(initiatedTimeout);
      clearTimeout(confirmingTimeout);
      clearTimeout(delayedTimeout);
    };
  }, [step]);

  return (
    <>
      <ModalBackButton onClick={() => onBack()} />

      <div className="mt-2">
        <div className="flex flex-col items-center justify-center">
          <img
            src={"https://static.buildwithglide.com/logos/coinbase_48a7e6c.svg"}
            className="mt-4 h-8 w-8 rounded-full"
            alt={`Coinbase Logo`}
          />
        </div>
      </div>

      {step === "mfa_code" || step === "transferring" ? (
        <>
          <div>
            <div className="mt-6 mb-2 font-medium text-sm text-[--color-text-primary]">
              Enter your Coinbase 2FA code
            </div>

            <input
              className="w-full bg-[--color-background-card] text-[--color-text-primary] py-3 px-4 rounded-lg border border-[--color-modal-border] ring-0 outline-none text-sm"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder={"000000"}
              autoComplete="off"
              disabled={step !== "mfa_code"}
            />

            <PrimaryButton
              className="mt-4"
              loading={step === "transferring"}
              disabled={
                step !== "mfa_code" || mfaCode.length === 0 || isBelowMinAmount
              }
              onClick={onTransfer}
            >
              {isBelowMinAmount
                ? `Min amount $${minAmountUSD.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : `Transfer ${session.paymentAmount} ${session.paymentCurrencySymbol}`}
            </PrimaryButton>

            {errorMessage && <Warning className="mt-4">{errorMessage}</Warning>}
          </div>

          <hr className="my-4 border-0.5 border-[--color-background-secondary]" />
        </>
      ) : null}

      {step === "mfa_code" || step === "transferring" ? (
        <Alert
          icon={<LockIcon strokeWidth={2.25} className="w-4 h-4" />}
          title="Enter 2FA Code"
          subtitle="Check your SMS, email, or 2FA app for the code."
        />
      ) : step === "waiting_for_transfer" && paymentStatus === "unpaid" ? (
        <div className="mt-4">
          <Alert
            id={`coinbase_wait_${waitingCopyStage}`}
            icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
            title={
              waitingCopyStage === "initial"
                ? "Transferring..."
                : waitingCopyStage === "initiated"
                  ? "Transfer initiated"
                  : waitingCopyStage === "confirming"
                    ? "Confirming your transaction"
                    : "Taking longer than expected"
            }
            subtitle={
              waitingCopyStage === "initial" ? (
                "We're completing the transfer. This can take a few minutes after you have initiated it."
              ) : waitingCopyStage === "initiated" ? (
                "Your transfer has been initiated. We're waiting to detect your transaction on the network."
              ) : waitingCopyStage === "confirming" ? (
                "Confirming your transaction. This can take a few seconds."
              ) : (
                <>
                  This is taking a bit longer than expected, but should complete
                  shortly. For support,{" "}
                  <a
                    href="https://docs.glide.link/support"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                  >
                    click here
                  </a>
                  .
                </>
              )
            }
          />
        </div>
      ) : step === "waiting_for_transfer" &&
        paymentStatus === "waiting_confirmations" ? (
        <Alert
          className="mt-4"
          icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
          title="Transfer detected"
          subtitle="We detected your transfer. Hold on for another minute while we finalize it."
        />
      ) : null}
    </>
  );
}
